import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
    getTemplateCrossProducts,
    updateTemplateCrossProducts,
    updateTemplateCrossProductsMeta,
} from "../api/scriptsApi";
import ItemEditor from "../components/ItemEditor";
import { toFormData } from "../features/scripts";
import {
    useBaseTemplateDetail,
    useBaseTemplateDraft,
    useDeleteTemplateOverride,
    usePublishVersion,
    useSaveBaseTemplateStructure,
    useSaveStructure,
    useSaveTemplateOverride,
    useScriptDraft,
    useScriptTemplateOverride,
    useScriptTemplateResolve,
} from "../hooks/useScriptDraft";
import ProductInfoModal from "../shared/editable-script-details/ProductInfoModal";
import fetchWithAuth from "../util/fetchWithAuth";
import { API_URL } from "../util/url";
import Color from "../widgets/operator/scripts/ProductDetailsContent/Color";
import ProductImg from "../widgets/operator/scripts/ProductDetailsContent/ProductImg";

const createLocalId = (() => {
  let counter = 0;
  return () => `tmp-${Date.now()}-${counter++}`;
})();

const mapDraftToEditor = (version) => ({
  ...version,
  sections: (version.sections || []).map((section) => ({
    ...section,
    localId: section.id != null ? `s-${section.id}` : createLocalId(),
    items: (section.items || []).map((item) => ({
      ...item,
      localId: item.id != null ? `i-${item.id}` : createLocalId(),
      tags: item.tags || [],
    })),
  })),
});

const reindexItems = (items) =>
  items.map((item, index) => ({ ...item, order: index }));

const reindexSections = (sections) =>
  sections.map((section, index) => ({
    ...section,
    order: index,
    items: reindexItems(section.items),
  }));

const buildPayload = (version) => ({
  version_id: version.id,
  sections: version.sections.map((section) => ({
    title: section.title,
    order: section.order,
    is_collapsed_by_default: section.is_collapsed_by_default,
    items: section.items.map((item) => ({
      item_type: item.item_type,
      title: item.title,
      body: item.body,
      order: item.order,
      tags: item.tags || [],
    })),
  })),
});

const validateEditorVersion = (version) => {
  for (const section of version?.sections || []) {
    const sectionName = section.title || "Без назви";
    for (const item of section.items || []) {
      if (!String(item.item_type || "").trim()) {
        return `Оберіть тип елемента у секції "${sectionName}".`;
      }
      if (!String(item.title || "").trim()) {
        return `Заповніть заголовок елемента у секції "${sectionName}".`;
      }
      if (!String(item.body || "").trim()) {
        return `Заповніть текст елемента "${item.title || "Без заголовка"}".`;
      }
    }
  }
  return null;
};

const mapOverrideToEditor = (override, templateId) =>
  mapDraftToEditor({
    id: null,
    template: templateId ?? null,
    version: 0,
    status: "DRAFT",
    sections: override?.sections || [],
  });

const buildOverridePayload = (version, params) => ({
  crm: params.crmId,
  kind: params.kind,
  template_id:
    Number.isFinite(params.templateId) ? params.templateId : undefined,
  product_id: String(params.productId),
  sections: version.sections.map((section) => ({
    title: section.title,
    order: section.order,
    is_collapsed_by_default: section.is_collapsed_by_default,
    items: section.items.map((item) => ({
      item_type: item.item_type,
      title: item.title,
      body: item.body,
      order: item.order,
      tags: item.tags || [],
    })),
  })),
});

const buildOverrideQueryFromParams = ({ kind, crmId, productId }) => {
  if (!kind || crmId === undefined || crmId === null || productId === undefined || productId === null) {
    return "";
  }
  return `?kind=${encodeURIComponent(kind)}&crm=${encodeURIComponent(
    crmId
  )}&product_id=${encodeURIComponent(productId)}`;
};

const normalizeItemType = (item) =>
  String(item?.item_type || "").toLowerCase();

const sortByOrder = (a, b) =>
  Number(a?.order ?? 0) - Number(b?.order ?? 0);

const CHARACTERISTICS_ITEM_TYPE = "characteristics";
const CHARACTERISTICS_SECTION_TITLE = "Характеристики";

const getTemplateCharacteristics = (sections) => {
  for (const section of sections || []) {
    const item = (section.items || []).find(
      (entry) => normalizeItemType(entry) === CHARACTERISTICS_ITEM_TYPE
    );
    if (item) return item.body || "";
  }
  return "";
};

const upsertTemplateCharacteristics = (sections, body) => {
  let found = false;
  const nextSections = (sections || []).map((section) => {
    const items = section.items || [];
    const nextItems = items.map((item) => {
      if (normalizeItemType(item) !== CHARACTERISTICS_ITEM_TYPE) return item;
      found = true;
      return {
        ...item,
        title: item.title || CHARACTERISTICS_SECTION_TITLE,
        body,
      };
    });
    return { ...section, items: nextItems };
  });

  if (found) return nextSections;

  return [
    ...nextSections,
    {
      localId: createLocalId(),
      title: CHARACTERISTICS_SECTION_TITLE,
      order: nextSections.length,
      is_collapsed_by_default: false,
      items: [
        {
          localId: createLocalId(),
          item_type: CHARACTERISTICS_ITEM_TYPE,
          title: CHARACTERISTICS_SECTION_TITLE,
          body,
          order: 0,
          tags: [],
        },
      ],
    },
  ];
};

const normalizeCrossProducts = (items) =>
  Array.isArray(items)
    ? items.map((item) => {
        if (typeof item === "string" || typeof item === "number") {
          return { product_id: item };
        }
        return item;
      })
    : [];

const normalizeCrossMeta = (items) => {
  if (!Array.isArray(items)) return {};
  return items.reduce((acc, item) => {
    const key = item?.product_id ?? item?.id ?? null;
    if (key == null) return acc;
    acc[String(key)] = item?.short_description ?? "";
    return acc;
  }, {});
};

const CrossMetaInput = React.memo(
  ({ value, onCommit, disabled = false, placeholder = "Опис відсутній" }) => {
    const [localValue, setLocalValue] = useState(value || "");
    const commitTimerRef = useRef(null);

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    useEffect(() => {
      return () => {
        if (commitTimerRef.current) {
          window.clearTimeout(commitTimerRef.current);
        }
      };
    }, []);

    const scheduleCommit = useCallback(
      (nextValue) => {
        if (commitTimerRef.current) {
          window.clearTimeout(commitTimerRef.current);
        }
        commitTimerRef.current = window.setTimeout(() => {
          onCommit(nextValue);
          commitTimerRef.current = null;
        }, 350);
      },
      [onCommit]
    );

    const handleChange = (event) => {
      const nextValue = event.target.value;
      setLocalValue(nextValue);
      scheduleCommit(nextValue);
    };

    const handleBlur = () => {
      if (commitTimerRef.current) {
        window.clearTimeout(commitTimerRef.current);
        commitTimerRef.current = null;
      }
      onCommit(localValue);
    };

    return (
      <TextField
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        variant="standard"
        fullWidth
        disabled={disabled}
        InputProps={{ disableUnderline: true }}
        inputProps={{ title: localValue }}
        sx={{
          mt: 0.25,
          "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ".dark-theme &": {
              color: "rgba(255,255,255,0.72)",
            },
          },
        }}
      />
    );
  }
);

const CrossPriceInput = React.memo(
  ({ value, onCommit, disabled = false, placeholder = "Ціна" }) => {
    const [localValue, setLocalValue] = useState(
      value != null ? String(value) : ""
    );

    useEffect(() => {
      setLocalValue(value != null ? String(value) : "");
    }, [value]);

    const handleChange = (event) => {
      setLocalValue(event.target.value);
    };

    const handleBlur = () => {
      onCommit(localValue.trim());
    };

    return (
      <TextField
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        variant="standard"
        fullWidth
        disabled={disabled}
        InputProps={{ disableUnderline: true }}
        inputProps={{ inputMode: "decimal" }}
        sx={{
          width: "100%",
          maxWidth: 120,
          ml: "auto",
          "& .MuiInputBase-input": {
            fontSize: "0.8rem",
            textAlign: "right",
            color: "rgba(16,185,129,0.9)",
            backgroundColor: "rgba(16,185,129,0.12)",
            borderRadius: "999px",
            padding: "2px 10px",
            fontWeight: 600,
            width: "100%",
            ".dark-theme &": {
              color: "rgba(167,243,208,1)",
              backgroundColor: "rgba(16,185,129,0.22)",
            },
          },
        }}
      />
    );
  }
);

const mergeCrossProducts = (sources) => {
  const result = [];
  const seen = new Set();
  sources.forEach((list) => {
    (list || []).forEach((item) => {
      if (!item) return;
      const key =
        item.cross_id ?? item.id ?? item.product_id ?? item.title ?? null;
      if (key == null) {
        result.push(item);
        return;
      }
      const keyValue = String(key);
      if (seen.has(keyValue)) return;
      seen.add(keyValue);
      result.push(item);
    });
  });
  return result;
};

const getCrossKey = (item) => {
  if (!item) return null;
  const candidates = [
    item.product_id,
    item.cross_id,
    item.script_product_id,
    item.id,
    item.title,
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const key = String(candidate).trim();
    if (key) return key;
  }
  return null;
};

const buildSectionsByTypes = (sections, types) => {
  const typeSet = new Set(types.map((type) => type.toLowerCase()));
  return (sections || [])
    .map((section) => {
      const items = (section.items || [])
        .filter((item) => typeSet.has(normalizeItemType(item)))
        .slice()
        .sort(sortByOrder);
      if (!items.length) return null;
      return { section, items };
    })
    .filter(Boolean)
    .sort((a, b) => sortByOrder(a.section, b.section));
};

const formatErrorMessage = (error) => {
  if (error instanceof Error) {
    return error.message;
  }
  return "Невідома помилка";
};

const isOverrideUniqueError = (error) => {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("unique set") ||
    message.includes("non_field_errors") ||
    message.includes("unique")
  );
};

const parseResponseError = async (response) => {
  const text = await response.text();
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      const data = JSON.parse(text);
      return data?.detail || data?.message || JSON.stringify(data);
    } catch {
      return "Помилка сервера під час збереження товару";
    }
  }
  if (text && !text.trim().startsWith("<")) {
    return text;
  }
  return "Помилка сервера під час збереження товару";
};

const normalizeColorId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeColorDraft = (color) => ({
  id: normalizeColorId(color?.id),
  name: color?.name || "",
  hex: color?.hex || "#000000",
  quantity: color?.quantity != null ? String(color.quantity) : "0",
});

const ColorsDialog = ({ open, colors, onClose, onApply }) => {
  const [draft, setDraft] = useState(
    () => (colors || []).map(normalizeColorDraft)
  );
  const [errors, setErrors] = useState([]);
  const colorsRef = useRef(colors || []);
  const draftRef = useRef((colors || []).map(normalizeColorDraft));

  useEffect(() => {
    colorsRef.current = colors || [];
  }, [colors]);

  useEffect(() => {
    if (open) {
      const next = colorsRef.current.map(normalizeColorDraft);
      setDraft(next);
      draftRef.current = next;
      setErrors([]);
    }
  }, [open]);

  const handleAddColor = () => {
    setDraft((prev) => {
      const next = [...prev, { name: "", hex: "#000000", quantity: "0" }];
      draftRef.current = next;
      return next;
    });
  };

  const handleDeleteColor = (index) => {
    setDraft((prev) => {
      const next = prev.filter((_, i) => i !== index);
      draftRef.current = next;
      return next;
    });
  };

  const handleFieldInput = (index, field, value) => {
    const next = draftRef.current.slice();
    const current = next[index] || normalizeColorDraft();
    next[index] = { ...current, [field]: value };
    draftRef.current = next;
    if (field === "name") {
      setErrors((prev) => {
        if (!prev.length) return prev;
        const nextErrors = prev.slice();
        nextErrors[index] = !String(value || "").trim();
        return nextErrors;
      });
    }
  };

  const handleApply = () => {
    const rawColors = draftRef.current || [];
    const nextErrors = rawColors.map(
      (color) => !String(color?.name || "").trim()
    );
    if (nextErrors.some(Boolean)) {
      setErrors(nextErrors);
      setDraft(rawColors.slice());
      return;
    }
    const nextColors = rawColors.map((color) => ({
      id: normalizeColorId(color.id),
      name: color.name.trim(),
      hex: color.hex || "#000000",
      quantity: Math.max(0, Number(color.quantity) || 0),
    }));
    onApply(nextColors);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Кольори</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {draft.map((color, index) => (
            <Box
              key={`${color.id || "new"}-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 120px 120px auto" },
                gap: 1,
                alignItems: "center",
              }}
              >
                <TextField
                  label="Назва"
                  defaultValue={color.name}
                  onInput={(event) =>
                    handleFieldInput(index, "name", event.target.value)
                  }
                  error={Boolean(errors[index])}
                  helperText={errors[index] ? "Вкажіть назву" : " "}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    HEX
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    defaultValue={color.hex}
                    onInput={(event) =>
                      handleFieldInput(index, "hex", event.target.value)
                    }
                    sx={{
                      width: 120,
                      height: 40,
                    border: "1px solid rgba(0,0,0,0.23)",
                    borderRadius: 1,
                    background: "transparent",
                    padding: 0,
                  }}
                />
                </Box>
                <TextField
                  label="Кількість"
                  type="number"
                  defaultValue={color.quantity}
                  onInput={(event) =>
                    handleFieldInput(index, "quantity", event.target.value)
                  }
                />
              <IconButton
                color="error"
                onClick={() => handleDeleteColor(index)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddColor}
          >
            Додати колір
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleApply}>
          Зберегти
        </Button>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
};

const ScriptTemplateEditorPage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const templateIdParam = searchParams.get("template_id");
  const templateSource =
    searchParams.get("template_source") || searchParams.get("source");
  const routeTemplateId = Number(params.templateId);
  const queryTemplateId = templateIdParam ? Number(templateIdParam) : NaN;
  const templateId = Number.isFinite(routeTemplateId)
    ? routeTemplateId
    : queryTemplateId;
  const kind = searchParams.get("kind") || "client_card";
  const crmParam = searchParams.get("crm");
  const productParam = searchParams.get("product_id");
  const scriptIdParam =
    searchParams.get("script_id") || searchParams.get("script_product_id");
  const crmId = crmParam ? Number(crmParam) : NaN;
  const productId = productParam || "";
  const scriptProductId = scriptIdParam ? Number(scriptIdParam) : NaN;
  const previewProductId =
    searchParams.get("script_id") ||
    searchParams.get("script_product_id") ||
    productId;

  const hasResolveParams =
    Boolean(kind) && Number.isFinite(crmId) && Boolean(productId);
  const isBaseTemplateMode = templateSource === "templates";
  const previewUrl =
    hasResolveParams && previewProductId
      ? `/operator/scripts/${previewProductId}?crm=${crmId}&product_id=${productId}`
      : "";

  const {
    data: resolvedData,
    isLoading: isResolveLoading,
    error: resolveError,
    refetch: refetchResolve,
  } = useScriptTemplateResolve(
    hasResolveParams
      ? {
          kind,
          crmId,
          productId,
        }
      : undefined
  );

  const {
    data: overrideData,
    isLoading: isOverrideLoading,
    error: overrideError,
    refetch: refetchOverride,
  } = useScriptTemplateOverride(
    hasResolveParams
      ? {
          kind,
          crmId,
          productId,
        }
      : undefined
  );

  const scriptDraftQuery = useScriptDraft(
    !hasResolveParams && !isBaseTemplateMode && Number.isFinite(templateId)
      ? templateId
      : undefined
  );

  const baseDraftQuery = useBaseTemplateDraft(
    !hasResolveParams && isBaseTemplateMode && Number.isFinite(templateId)
      ? templateId
      : undefined
  );
  const baseTemplateQuery = useBaseTemplateDetail(
    !hasResolveParams && isBaseTemplateMode && Number.isFinite(templateId)
      ? templateId
      : undefined
  );

  const draftData = isBaseTemplateMode
    ? baseDraftQuery.data
    : scriptDraftQuery.data;
  const isDraftLoading = isBaseTemplateMode
    ? baseDraftQuery.isLoading
    : scriptDraftQuery.isLoading;
  const draftError = isBaseTemplateMode
    ? baseDraftQuery.error
    : scriptDraftQuery.error;
  const refetchDraft = isBaseTemplateMode
    ? baseDraftQuery.refetch
    : scriptDraftQuery.refetch;
  const [baseTemplateLocal, setBaseTemplateLocal] = useState(null);
  const [baseTemplateCrossProductsLocal, setBaseTemplateCrossProductsLocal] =
    useState(null);
  const [baseTemplateCrossMeta, setBaseTemplateCrossMeta] = useState(null);
  const baseTemplate = isBaseTemplateMode ? baseTemplateLocal : null;

  useEffect(() => {
    if (!isBaseTemplateMode) return;
    setBaseTemplateLocal(baseTemplateQuery.data || null);
  }, [baseTemplateQuery.data, isBaseTemplateMode]);
  const effectiveCrmId = Number.isFinite(crmId)
    ? crmId
    : isBaseTemplateMode && baseTemplate?.crm != null
    ? Number(baseTemplate.crm)
    : NaN;

  const resolvedTemplateId = resolvedData?.template?.id;
  const saveMutation = useSaveStructure(
    !isBaseTemplateMode
      ? Number.isFinite(resolvedTemplateId)
        ? resolvedTemplateId
        : templateId
      : undefined
  );
  const saveBaseMutation = useSaveBaseTemplateStructure(
    isBaseTemplateMode && Number.isFinite(templateId) ? templateId : undefined
  );
  const saveOverrideMutation = useSaveTemplateOverride();
  const deleteOverrideMutation = useDeleteTemplateOverride();
  const publishMutation = usePublishVersion();
  const isOverrideActive = Boolean(overrideData);
  const overrideVersion = useMemo(
    () =>
      overrideData
        ? mapOverrideToEditor(overrideData, resolvedTemplateId)
        : null,
    [overrideData, resolvedTemplateId]
  );

  const [editorVersion, setEditorVersion] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [scriptTab, setScriptTab] = useState(0);
  const [expandedSectionId, setExpandedSectionId] = useState(null);
  const [productData, setProductData] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [productCharacteristics, setProductCharacteristics] = useState("");
  const [productAttributes, setProductAttributes] = useState([]);
  const [productColors, setProductColors] = useState([]);
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState("");
  const [productImageRemoved, setProductImageRemoved] = useState(false);
  const [isImageDragOver, setIsImageDragOver] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogError, setImageDialogError] = useState(null);
  const [productModalType, setProductModalType] = useState(null);
  const [selectedAttrIndex, setSelectedAttrIndex] = useState(null);
  const [colorsDialogOpen, setColorsDialogOpen] = useState(false);
  const [attrMenuAnchor, setAttrMenuAnchor] = useState(null);
  const [productQuantity, setProductQuantity] = useState(0);
  const [productPrice, setProductPrice] = useState("");
  const [isProductSaving, setIsProductSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [crossInput, setCrossInput] = useState("");
  const [selectedCrossOptions, setSelectedCrossOptions] = useState([]);
  const [crossAdd, setCrossAdd] = useState([]);
  const [crossRemove, setCrossRemove] = useState([]);
  const [crossMeta, setCrossMeta] = useState({});
  const [crossPriceOverrides, setCrossPriceOverrides] = useState({});
  const [crmProducts, setCrmProducts] = useState([]);
  const [overrideCrossProductsLocal, setOverrideCrossProductsLocal] = useState(null);

  const editorVersionRef = useRef(null);
  const saveStateRef = useRef({ inFlight: false, pending: false });
  const productModalRef = useRef(null);
  const dirtyRef = useRef(false);

  const markDirty = useCallback(() => {
    if (dirtyRef.current) return;
    dirtyRef.current = true;
    setIsDirty(true);
  }, []);

  useEffect(() => {
    const nextVersion = overrideVersion
      ? overrideVersion
      : hasResolveParams
        ? resolvedData?.version
        : draftData;

    if (nextVersion) {
      const mapped =
        nextVersion === overrideVersion
          ? nextVersion
          : mapDraftToEditor(nextVersion);
      setEditorVersion(mapped);
      const firstSection = mapped.sections?.[0];
      setSelectedSectionId((prev) => prev ?? firstSection?.localId ?? null);
      setIsDirty(false);
      dirtyRef.current = false;
      setCrossAdd([]);
      setCrossRemove([]);
      setCrossInput("");
      setErrorMessage(null);
    }
  }, [draftData, resolvedData, hasResolveParams, overrideVersion]);

  useEffect(() => {
    editorVersionRef.current = editorVersion;
  }, [editorVersion]);

  useEffect(() => {
    if (overrideData && Array.isArray(overrideData.cross_products)) {
      setOverrideCrossProductsLocal(overrideData.cross_products);
      return;
    }
    if (!overrideData) {
      setOverrideCrossProductsLocal(null);
    }
  }, [overrideData]);

  useEffect(() => {
    if (dirtyRef.current) return;
    const metaSource = isBaseTemplateMode
      ? baseTemplateCrossMeta ?? baseTemplate?.cross_products_meta ?? baseTemplate?.cross_products
      : overrideData?.cross_products_meta ??
        resolvedData?.cross_products_meta ??
        resolvedData?.template?.cross_products_meta ??
        resolvedData?.version?.cross_products_meta ??
        overrideData?.cross_products ??
        resolvedData?.cross_products ??
        resolvedData?.template?.cross_products ??
        resolvedData?.version?.cross_products;
    setCrossMeta(normalizeCrossMeta(metaSource));
  }, [baseTemplate, baseTemplateCrossMeta, overrideData, resolvedData, isBaseTemplateMode]);

  useEffect(() => {
    if (!isBaseTemplateMode || !Number.isFinite(templateId)) return;
    let isActive = true;
    const load = async () => {
      try {
        const items = await getTemplateCrossProducts(templateId);
        if (!isActive) return;
        const list = Array.isArray(items) ? items : [];
        setBaseTemplateCrossProductsLocal(list);
        setBaseTemplateCrossMeta(list);
      } catch {
        if (!isActive) return;
        setBaseTemplateCrossProductsLocal([]);
        setBaseTemplateCrossMeta([]);
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [isBaseTemplateMode, templateId]);

  useEffect(() => {
    if (!hasResolveParams) {
      setProductData(null);
      return;
    }

    let isActive = true;

    const loadProduct = async () => {
      setProductLoading(true);
      setProductError(null);
      try {
        let product = null;
        if (Number.isFinite(scriptProductId) && scriptProductId > 0) {
          const response = await fetchWithAuth(
            `${API_URL}/scripts/products/${scriptProductId}/`
          );
          if (!response.ok) {
            throw new Error("Помилка запиту продукту");
          }
          product = await response.json();
        } else if (Number.isFinite(crmId) && productId) {
          const response = await fetchWithAuth(
            `${API_URL}/scripts/products/by-crm/${crmId}/`
          );
          if (!response.ok) {
            throw new Error("Помилка запиту продуктів CRM");
          }
          const list = await response.json();
          if (isActive) {
            setCrmProducts(Array.isArray(list) ? list : []);
          }
          product = Array.isArray(list)
            ? list.find(
                (item) => String(item.product_id) === String(productId)
              )
            : null;
        }

        if (isActive) {
          setProductData(product || null);
        }
      } catch (error) {
        if (isActive) {
          setProductError(error);
        }
      } finally {
        if (isActive) {
          setProductLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      isActive = false;
    };
  }, [hasResolveParams, crmId, productId, scriptProductId]);

  useEffect(() => {
    if (!Number.isFinite(effectiveCrmId)) return;
    if (crmProducts.length) return;
    let isActive = true;

    const loadCrmProducts = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/scripts/products/by-crm/${effectiveCrmId}/`
        );
        if (!response.ok) return;
        const list = await response.json();
        if (isActive) {
          setCrmProducts(Array.isArray(list) ? list : []);
        }
      } catch {
        if (isActive) setCrmProducts([]);
      }
    };

    loadCrmProducts();

    return () => {
      isActive = false;
    };
  }, [effectiveCrmId, crmProducts.length]);

  useEffect(() => {
    setSelectedColor(productColors?.[0] || null);
  }, [productColors]);

  useEffect(() => {
    if (!productData) return;
    setProductCharacteristics(productData.characteristics || "");
    setProductQuantity(productData.total_quantity || 0);
    setProductPrice(
      productData.price != null ? String(productData.price) : ""
    );
    if (Array.isArray(productData.attributes)) {
      setProductAttributes(
        productData.attributes.map((attr) => ({
          name: attr.name,
          variations: (attr.values || []).map((value) => value.value ?? value),
        }))
      );
    } else {
      setProductAttributes([]);
    }
    if (Array.isArray(productData.colors)) {
      setProductColors(
        productData.colors.map((color) => ({
          id: normalizeColorId(color.id),
          name: color.name || "",
          hex: color.hex || "#000000",
          quantity: Number(color.quantity ?? 0),
        }))
      );
    } else {
      setProductColors([]);
    }
    setProductImageRemoved(false);
  }, [productData]);

  useEffect(() => {
    if (productData) return;
    setProductCharacteristics(
      getTemplateCharacteristics(editorVersion?.sections || [])
    );
  }, [editorVersion?.sections, productData]);

  useEffect(() => {
    if (!productImageFile) {
      setProductImagePreview("");
      return undefined;
    }
    const nextUrl = URL.createObjectURL(productImageFile);
    setProductImagePreview(nextUrl);
    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [productImageFile]);

  const updateSections = useCallback((updater) => {
    setEditorVersion((prev) => {
      if (!prev) return prev;
      const nextSections = reindexSections(updater(prev.sections));
      if (!nextSections.find((section) => section.localId === selectedSectionId)) {
        setSelectedSectionId(nextSections[0]?.localId ?? null);
      }
      return { ...prev, sections: nextSections };
    });
    markDirty();
  }, [selectedSectionId, markDirty]);

  const updateSectionDraft = useCallback(
    (sectionLocalId, updates) => {
      const current = editorVersionRef.current;
      if (!current) return;
      const section = current.sections?.find(
        (item) => item.localId === sectionLocalId
      );
      if (!section) return;
      Object.assign(section, updates);
      markDirty();
    },
    [markDirty]
  );

  const updateItemDraft = useCallback(
    (sectionLocalId, itemLocalId, updates) => {
      const current = editorVersionRef.current;
      if (!current) return;
      const section = current.sections?.find(
        (item) => item.localId === sectionLocalId
      );
      if (!section) return;
      const target = section.items?.find(
        (item) => item.localId === itemLocalId
      );
      if (!target) return;
      Object.assign(target, updates);
      markDirty();
    },
    [markDirty]
  );

  const handleAddSection = (defaultItemType) => {
    const localId = createLocalId();
    const initialItems = defaultItemType
      ? [
          {
            localId: createLocalId(),
            item_type: defaultItemType,
            title: "",
            body: "",
            order: 0,
            tags: [],
          },
        ]
      : [];
    updateSections((sections) => [
      ...sections,
      {
        localId,
        title: "",
        order: sections.length,
        is_collapsed_by_default: false,
        items: initialItems,
      },
    ]);
    setSelectedSectionId(localId);
  };

  const handleUpdateSection = (sectionLocalId, updates) => {
    updateSections((sections) =>
      sections.map((section) =>
        section.localId === sectionLocalId ? { ...section, ...updates } : section
      )
    );
  };

  const handleDeleteSection = (sectionLocalId) => {
    updateSections((sections) => {
      const index = sections.findIndex(
        (section) => section.localId === sectionLocalId
      );
      const nextSections = sections.filter(
        (section) => section.localId !== sectionLocalId
      );
      const nextSelected =
        nextSections[index] || nextSections[index - 1] || nextSections[0];
      setSelectedSectionId(nextSelected?.localId ?? null);
      return nextSections;
    });
  };

  const handleMoveSectionInGroup = useCallback(
    (sectionLocalId, direction, groupIds) => {
      const currentIndex = groupIds.indexOf(sectionLocalId);
      if (currentIndex === -1) return;
      const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= groupIds.length) return;
      const targetId = groupIds[targetIndex];
      updateSections((sections) => {
        const sourceIndex = sections.findIndex(
          (section) => section.localId === sectionLocalId
        );
        const destIndex = sections.findIndex(
          (section) => section.localId === targetId
        );
        if (sourceIndex === -1 || destIndex === -1) return sections;
        const next = [...sections];
        [next[sourceIndex], next[destIndex]] = [next[destIndex], next[sourceIndex]];
        return next;
      });
    },
    [updateSections]
  );

  const handleAddItem = (sectionLocalId, defaultItemType) =>
    updateSections((sections) =>
      sections.map((section) => {
        if (section.localId !== sectionLocalId) return section;
        const nextItems = [
          ...section.items,
          {
            localId: createLocalId(),
            item_type: defaultItemType || "",
            title: "",
            body: "",
            order: section.items.length,
            tags: [],
          },
        ];
        return { ...section, items: nextItems };
      })
    );

  const handleUpdateItem = (sectionLocalId, itemLocalId, updates) =>
    updateSections((sections) =>
      sections.map((section) => {
        if (section.localId !== sectionLocalId) return section;
        const nextItems = section.items.map((item) =>
          item.localId === itemLocalId ? { ...item, ...updates } : item
        );
        return { ...section, items: nextItems };
      })
    );

  const handleDeleteItem = (sectionLocalId, itemLocalId) =>
    updateSections((sections) =>
      sections.map((section) => {
        if (section.localId !== sectionLocalId) return section;
        return {
          ...section,
          items: section.items.filter((item) => item.localId !== itemLocalId),
        };
      })
    );

  const handleMoveItem = (sectionLocalId, itemLocalId, direction) =>
    updateSections((sections) =>
      sections.map((section) => {
        if (section.localId !== sectionLocalId) return section;
        const index = section.items.findIndex(
          (item) => item.localId === itemLocalId
        );
        if (index === -1) return section;
        const targetIndex = direction === "up" ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= section.items.length) {
          return section;
        }
        const nextItems = [...section.items];
        [nextItems[index], nextItems[targetIndex]] = [
          nextItems[targetIndex],
          nextItems[index],
        ];
        return { ...section, items: nextItems };
      })
    );

  const handleAccordionChange = (sectionId) => (_, nextExpanded) => {
    setExpandedSectionId(nextExpanded ? sectionId : null);
  };

  const handleScriptTabChange = (_, nextTab) => {
    setScriptTab(nextTab);
  };

  const handleProductImageFile = (file) => {
    if (!file) return;
    if (file.type && !file.type.startsWith("image/")) {
      setImageDialogError("Підтримуються тільки зображення");
      setToast({
        open: true,
        message: "Підтримуються тільки зображення",
        severity: "error",
      });
      return;
    }
    setImageDialogError(null);
    setProductImageRemoved(false);
    setProductImageFile(file);
  };

  const handleProductImageSelect = (event) => {
    const file = event.target.files?.[0];
    handleProductImageFile(file || null);
  };

  const handleImagePaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items?.length) return;
    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        event.preventDefault();
        handleProductImageFile(item.getAsFile());
        return;
      }
    }
  };

  const handleImageDrop = (event) => {
    event.preventDefault();
    setIsImageDragOver(false);
    const file = event.dataTransfer?.files?.[0];
    handleProductImageFile(file || null);
  };

  const handleImageDragOver = (event) => {
    event.preventDefault();
    if (!isImageDragOver) {
      setIsImageDragOver(true);
    }
  };

  const handleImageDragLeave = () => {
    setIsImageDragOver(false);
  };

  const handleOpenImageDialog = () => {
    setImageDialogError(null);
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setIsImageDragOver(false);
    setImageDialogOpen(false);
  };

  const handleRemoveProductImage = () => {
    setProductImageRemoved(true);
    setProductImageFile(null);
    setProductImagePreview("");
    setProductData((prev) => (prev ? { ...prev, image: null } : prev));
  };

  const handleOpenProductModal = (type) => setProductModalType(type);

  const handleAddCrossProduct = () => {
    const nextIds = new Set();
    const trimmed = crossInput.trim();
    if (trimmed) {
      nextIds.add(trimmed);
    }
    selectedCrossOptions.forEach((option) => {
      if (typeof option === "string") {
        nextIds.add(option);
        return;
      }
      if (option?.product_id != null) {
        nextIds.add(String(option.product_id));
      }
    });

    if (!nextIds.size) return;

    const notFound = [];
    const toAdd = [];
    nextIds.forEach((id) => {
      if (Number.isFinite(effectiveCrmId) && !crmProductsMap.has(String(id))) {
        notFound.push(id);
        return;
      }
      if (crossAddedSet.has(String(id))) return;
      toAdd.push(String(id));
    });

    if (notFound.length) {
      setToast({
        open: true,
        message: `Товар не знайдено в цій CRM: ${notFound.join(", ")}`,
        severity: "error",
      });
    }

    if (!toAdd.length) {
      setCrossInput("");
      setSelectedCrossOptions([]);
      return;
    }

    setCrossAdd((prev) => {
      const existing = new Set(prev.map(String));
      const next = [...prev];
      toAdd.forEach((id) => {
        if (existing.has(id)) return;
        existing.add(id);
        next.push(id);
      });
      return next;
    });
    setCrossMeta((prev) => {
      const next = { ...prev };
      toAdd.forEach((id) => {
        if (next[id] != null) return;
        next[id] = crmProductsMap.get(String(id))?.short_description ?? "";
      });
      return next;
    });
    setCrossRemove((prev) =>
      prev.filter((item) => !toAdd.includes(String(item)))
    );
    setCrossInput("");
    setSelectedCrossOptions([]);
    markDirty();
  };

  const handleRemoveCrossProduct = (product) => {
    const key = getCrossKey(product);
    if (!key) return;
    setCrossAdd((prev) => prev.filter((item) => String(item) !== key));
    setCrossRemove((prev) =>
      prev.map(String).includes(key) ? prev : [...prev, key]
    );
    setCrossMeta((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    markDirty();
  };

  const handleCrossMetaChange = useCallback(
    (key, value) => {
      setCrossMeta((prev) => {
        if (prev[key] === value) return prev;
        return { ...prev, [key]: value };
      });
      markDirty();
    },
    [markDirty]
  );
  const handleCharacteristicsChange = useCallback(
    (value) => {
      setProductCharacteristics(value);
      if (productData?.id) {
        markDirty();
        return;
      }
      updateSections((sections) =>
        upsertTemplateCharacteristics(sections, value)
      );
    },
    [markDirty, productData?.id, updateSections]
  );
  const handleCloseProductModal = () => setProductModalType(null);
  const handleOpenAttrMenu = (event) => setAttrMenuAnchor(event.currentTarget);
  const handleCloseAttrMenu = () => setAttrMenuAnchor(null);
  const handleOpenColorsDialog = () => {
    setColorsDialogOpen(true);
    handleCloseAttrMenu();
  };
  const handleCloseColorsDialog = () => setColorsDialogOpen(false);

  const handleSaveProduct = async () => {
    if (!productData?.id) {
      setProductError(new Error("Немає ID товару для збереження"));
      return;
    }
    setProductError(null);
    setIsProductSaving(true);
    try {
      const preparedAttributes = productAttributes.map((attr) => {
        const originalAttr = (productData.attributes || []).find(
          (item) => item.name === attr.name
        );
        return {
          id: originalAttr?.id,
          name: attr.name,
          values: attr.variations
            .filter(Boolean)
            .map((value) => {
              const originalValue = originalAttr?.values?.find(
                (item) => item.value === value
              );
              return {
                id: originalValue?.id,
                value,
              };
            }),
        };
      });

      const payload = {
        characteristics: productCharacteristics,
        attributes: preparedAttributes,
      };
      const priceValue = String(productPrice ?? "").trim();
      if (priceValue !== "") {
        payload.price = priceValue;
      }

      payload.colors = productColors
        .map((color) => ({
          id: normalizeColorId(color.id),
          name: String(color.name || "").trim(),
          hex: color.hex,
          quantity: Number(color.quantity) || 0,
        }))
        .filter((color) => color.name);

      if (productImageFile) {
        payload.image = productImageFile;
      } else if (productImageRemoved) {
        payload.image = "";
      }

      const shouldUseFormData = productImageFile || productImageRemoved;
      const response = await fetchWithAuth(
        `${API_URL}/scripts/products/${productData.id}/`,
        {
          method: "PATCH",
          body: shouldUseFormData ? toFormData(payload) : payload,
        }
      );

      if (!response.ok) {
        const message = await parseResponseError(response);
        throw new Error(message);
      }

      const updated = await response.json();
      setProductData(updated);
      setProductImageFile(null);
      setProductImageRemoved(false);
      setToast({ open: true, message: "Товар збережено", severity: "success" });
    } catch (error) {
      setProductError(error);
      setToast({
        open: true,
        message: formatErrorMessage(error),
        severity: "error",
      });
    } finally {
      setIsProductSaving(false);
    }
  };

  const crmProductsMap = useMemo(() => {
    const map = new Map();
    (crmProducts || []).forEach((product) => {
      if (product?.product_id == null) return;
      map.set(String(product.product_id), product);
    });
    return map;
  }, [crmProducts]);

  const crmProductsById = useMemo(() => {
    const map = new Map();
    (crmProducts || []).forEach((product) => {
      if (product?.id == null) return;
      map.set(String(product.id), product);
    });
    return map;
  }, [crmProducts]);

  const resolveScriptProductId = useCallback(
    (product) => {
      const direct =
        product?.id ??
        product?.script_product_id ??
        product?.scripts_product_id ??
        product?.product_script_id;
      if (direct != null && Number.isFinite(Number(direct))) {
        const directId = Number(direct);
        if (!crmProductsById.size || crmProductsById.has(String(directId))) {
          return directId;
        }
      }
      const crmKey = product?.product_id ?? product?.crm_product_id ?? null;
      const mapped = crmKey != null ? crmProductsMap.get(String(crmKey)) : null;
      return mapped?.id ?? null;
    },
    [crmProductsById, crmProductsMap]
  );
  const handleCrossPriceChange = useCallback(
    async (product, nextValue) => {
      const cleaned = String(nextValue || "").replace(",", ".").trim();
      if (!cleaned) return;
      const priceNumber = Number(cleaned);
      if (!Number.isFinite(priceNumber)) {
        setToast({
          open: true,
          message: "Ціна має бути числом",
          severity: "error",
        });
        return;
      }
      const scriptProductId = resolveScriptProductId(product);
      if (!scriptProductId) {
        setToast({
          open: true,
          message: "Не вдалося визначити товар для оновлення ціни",
          severity: "error",
        });
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${API_URL}/scripts/products/${scriptProductId}/`,
          {
            method: "PATCH",
            body: toFormData({ price: priceNumber }),
          }
        );
        if (!response.ok) {
          const message = await parseResponseError(response);
          throw new Error(message);
        }
        const priceKey =
          product?.product_id ?? product?.script_product_id ?? product?.id;
        if (priceKey != null) {
          setCrossPriceOverrides((prev) => ({
            ...prev,
            [String(priceKey)]: cleaned,
          }));
        }
        setToast({
          open: true,
          message: "Ціну оновлено",
          severity: "success",
        });
      } catch (error) {
        setToast({
          open: true,
          message: formatErrorMessage(error),
          severity: "error",
        });
      }
    },
    [resolveScriptProductId]
  );

  const templateCrossProducts = normalizeCrossProducts(
    isBaseTemplateMode
      ? (baseTemplateCrossProductsLocal ?? baseTemplate?.cross_products ?? []).map((item) => {
          const rawId =
            typeof item === "object"
              ? item?.id ?? item?.product_id ?? item?.crm_product_id
              : item;
          if (rawId == null) return item;
          const key = String(rawId);
          const hasCrmProductId =
            typeof item === "object" &&
            (item?.product_id != null || item?.crm_product_id != null);
          const mapped = hasCrmProductId
            ? crmProductsMap.get(key) ?? crmProductsById.get(key)
            : crmProductsById.get(key) ?? crmProductsMap.get(key);
          if (mapped) {
            return {
              ...mapped,
              script_product_id: mapped.id,
              product_id: mapped.product_id,
              crm: mapped.crm,
              short_description:
                item?.short_description ?? mapped.short_description ?? "",
            };
          }
          const fallbackProductId =
            typeof item === "object"
              ? item?.product_id ?? item?.crm_product_id ?? null
              : null;
          const productId = fallbackProductId ?? rawId;
          return {
            product_id: productId,
            title: typeof item === "object" ? item?.title : undefined,
            short_description:
              typeof item === "object" ? item?.short_description ?? "" : "",
            script_product_id: rawId,
            id: rawId,
          };
        })
      : overrideCrossProductsLocal ??
          overrideData?.cross_products ??
          resolvedData?.cross_products ??
          resolvedData?.template?.cross_products ??
          resolvedData?.version?.cross_products
  );
  const productCrossProducts = normalizeCrossProducts(productData?.cross_products);
  const crossProducts = mergeCrossProducts([
    templateCrossProducts,
    productCrossProducts,
  ]);

  const crossOptions = useMemo(() => {
    return (crmProducts || [])
      .filter((product) => product?.product_id != null)
      .map((product) => ({
        ...product,
        label: `${product.title || "Без назви"} (#${product.product_id})`,
      }));
  }, [crmProducts]);

  const crossProductsForView = useMemo(() => {
    const removed = new Set((crossRemove || []).map((item) => String(item)));
    const normalize = (product) => {
      const crmProductId = product?.product_id ?? product?.crm_product_id ?? "";
      const mapped = crmProductsMap.get(String(crmProductId)) || {};
      const crmValue = product?.crm ?? mapped.crm ?? effectiveCrmId;
      return {
        ...mapped,
        ...product,
        product_id: product?.product_id ?? mapped.product_id ?? crmProductId,
        crm: crmValue,
        title: product?.title ?? mapped.title ?? "Без назви",
        image: product?.image ?? mapped.image ?? null,
        price: product?.price ?? mapped.price ?? null,
        short_description:
          product?.short_description ?? mapped.short_description ?? "",
        script_product_id:
          product?.script_product_id ??
          product?.scripts_product_id ??
          product?.product_script_id ??
          product?.id ??
          mapped.id,
      };
    };

    const base = (crossProducts || [])
      .map(normalize)
      .filter((product) => {
        const key = getCrossKey(product);
        if (key && removed.has(key)) return false;
        if (Number.isFinite(effectiveCrmId)) {
          const crmValue = product?.crm ?? effectiveCrmId;
          return Number(crmValue) === Number(effectiveCrmId);
        }
        return true;
      });

    const added = (crossAdd || [])
      .map((item) => String(item).trim())
      .filter(Boolean)
      .filter((item) => !removed.has(item))
      .map((productId) =>
        normalize({ product_id: productId, crm: effectiveCrmId })
      );

    return mergeCrossProducts([base, added]);
  }, [crossProducts, crossAdd, crossRemove, crmProductsMap, effectiveCrmId]);

  const crossAddedSet = useMemo(() => {
    const set = new Set();
    crossProductsForView.forEach((product) => {
      const key = product?.product_id ?? product?.crm_product_id ?? product?.id;
      if (key != null) set.add(String(key));
    });
    return set;
  }, [crossProductsForView]);

  const saveNow = useCallback(async () => {
    const currentVersion = editorVersionRef.current;
    if (!currentVersion) return;
    const validationError = validateEditorVersion(currentVersion);
    if (validationError) {
      setErrorMessage(validationError);
      setToast({ open: true, message: validationError, severity: "error" });
      throw new Error(validationError);
    }
    if (saveStateRef.current.inFlight) {
      saveStateRef.current.pending = true;
      return;
    }

    let savedVersion = null;
    saveStateRef.current.inFlight = true;
    setIsSaving(true);
    setErrorMessage(null);

    try {
      const overrideTemplateId =
        overrideData?.template ?? resolvedTemplateId;
      if (hasResolveParams) {
        const payload = buildOverridePayload(currentVersion, {
          crmId,
          kind,
          productId,
          templateId: overrideTemplateId,
        });
        const currentCrossIds = Array.from(
          new Set(
            crossProductsForView
              .map(
                (product) =>
                  product?.product_id ??
                  product?.crm_product_id ??
                  product?.cross_id ??
                  product?.id
              )
              .filter((value) => value !== undefined && value !== null && value !== "")
              .map(String)
          )
        );
        if (currentCrossIds.length) {
          payload.cross_products = currentCrossIds;
          payload.cross_products_add = currentCrossIds;
        } else if (crossAdd.length) {
          payload.cross_products_add = crossAdd;
        }
        if (crossRemove.length) {
          payload.cross_products_remove = crossRemove;
        }
        if (crossProductsForView.length) {
          payload.cross_products_meta = crossProductsForView
            .map((product) => {
              const key = String(
                product?.product_id ?? product?.cross_id ?? product?.id ?? ""
              );
              if (!key) return null;
              return {
                product_id: key,
                short_description: crossMeta[key] ?? "",
              };
            })
            .filter(Boolean);
        }
        let response;
        const overrideId = overrideData?.id ?? (overrideData ? 1 : null);
        const overrideQuery = buildOverrideQueryFromParams({
          kind,
          crmId,
          productId,
        });
        try {
          response = await saveOverrideMutation.mutateAsync({
            payload,
            overrideId,
            overrideQuery,
          });
        } catch (error) {
          if (!isOverrideUniqueError(error)) {
            throw error;
          }
          await deleteOverrideMutation.mutateAsync({ kind, crmId, productId });
          response = await saveOverrideMutation.mutateAsync({
            payload,
            overrideId: null,
            overrideQuery,
          });
        }
        setEditorVersion(mapOverrideToEditor(response, resolvedTemplateId));
        setOverrideCrossProductsLocal(
          Array.isArray(response.cross_products)
            ? response.cross_products
            : crossProductsForView
        );
        if (Array.isArray(response.cross_products_meta)) {
          setCrossMeta(normalizeCrossMeta(response.cross_products_meta));
        }
        await refetchOverride();
        const mapped = mapOverrideToEditor(response, resolvedTemplateId);
        editorVersionRef.current = mapped;
        setEditorVersion(mapped);
        savedVersion = mapped;
      } else {
        const payload = buildPayload(currentVersion);
        const response = isBaseTemplateMode
          ? await saveBaseMutation.mutateAsync(payload)
          : await saveMutation.mutateAsync(payload);
        const mapped = mapDraftToEditor(response);
        editorVersionRef.current = mapped;
        setEditorVersion(mapped);
        if (isBaseTemplateMode) {
          const resolvedCross = crossProductsForView
            .map((product) => ({
              product,
              id: resolveScriptProductId(product),
            }))
            .filter((item) => item.id != null);
          const validCrossProducts = resolvedCross.map((item) => item.product);
          const crossIds = Array.from(
            new Set(resolvedCross.map((item) => item.id))
          );
          const updatedTemplate = await updateTemplateCrossProducts(
            templateId,
            crossIds
          );
          setBaseTemplateCrossProductsLocal(validCrossProducts);
          const metaPayload = validCrossProducts
            .map((product) => {
              const key =
                product?.product_id ?? product?.crm_product_id ?? null;
              if (key == null) return null;
              return {
                product_id: String(key),
                short_description: crossMeta[String(key)] ?? "",
              };
            })
            .filter(Boolean);
          await updateTemplateCrossProductsMeta(templateId, metaPayload);
          setBaseTemplateLocal(updatedTemplate);
          setBaseTemplateCrossMeta(metaPayload);
          await baseTemplateQuery.refetch();
        }
        savedVersion = mapped;
      }
      setIsDirty(false);
      dirtyRef.current = false;
      setCrossAdd([]);
      setCrossRemove([]);
      setCrossInput("");
      setToast({ open: true, message: "Збережено", severity: "success" });
      return savedVersion;
    } catch (error) {
      setErrorMessage(formatErrorMessage(error));
      throw error;
    } finally {
      saveStateRef.current.inFlight = false;
      setIsSaving(false);
      if (saveStateRef.current.pending) {
        saveStateRef.current.pending = false;
        await saveNow();
      }
    }
  }, [
    saveMutation,
    saveBaseMutation,
    saveOverrideMutation,
    deleteOverrideMutation,
    hasResolveParams,
    isBaseTemplateMode,
    crmId,
    kind,
    productId,
    templateId,
    resolvedTemplateId,
    refetchOverride,
    overrideData,
    crossAdd,
    crossRemove,
    crossMeta,
    crossProductsForView,
    resolveScriptProductId,
    baseTemplateQuery,
    updateTemplateCrossProductsMeta,
  ]);


  const handlePublish = async () => {
    setErrorMessage(null);
    try {
      if (hasResolveParams) return;
      const savedVersion = await saveNow();
      const currentVersion = savedVersion || editorVersionRef.current;
      if (!currentVersion) return;
      await publishMutation.mutateAsync(currentVersion.id);
      setToast({ open: true, message: "Опубліковано", severity: "success" });
      if (hasResolveParams) {
        await refetchResolve();
      } else {
        await refetchDraft();
      }
    } catch (error) {
      setErrorMessage(formatErrorMessage(error));
    }
  };

  const handleSaveAndPublish = async () => {
    setErrorMessage(null);
    try {
      if (productData?.id) {
        await handleSaveProduct();
      }
      if (hasResolveParams) {
        await saveNow();
        return;
      }
      await handlePublish();
    } catch (error) {
      setErrorMessage(formatErrorMessage(error));
    }
  };

  const handleResetOverride = async () => {
    if (!hasResolveParams) return;
    setErrorMessage(null);
    try {
      await deleteOverrideMutation.mutateAsync({ kind, crmId, productId });
      setOverrideCrossProductsLocal(null);
      await refetchOverride();
      await refetchResolve();
      setToast({ open: true, message: "Скинуто до базового", severity: "success" });
    } catch (error) {
      setErrorMessage(formatErrorMessage(error));
    }
  };

  const isLoading =
    isResolveLoading ||
    isDraftLoading ||
    isOverrideLoading ||
    (isBaseTemplateMode && baseTemplateQuery.isLoading);
  const requestError =
    overrideError || resolveError || draftError || baseTemplateQuery.error;
  const templateSections = editorVersion?.sections || [];
  const productImageSrc = productImagePreview || productData?.image || "";
  const faqSections = buildSectionsByTypes(templateSections, ["faq", "note"]);
  const mainScriptSections = buildSectionsByTypes(templateSections, ["script"]);
  const objectionSections = buildSectionsByTypes(templateSections, ["objection"]);

  if (!hasResolveParams && !Number.isFinite(templateId)) {
    return <Typography>Invalid template id.</Typography>;
  }

  if (isLoading) {
    return <Typography>Loading draft...</Typography>;
  }

  if (requestError) {
    return <Alert severity="error">{formatErrorMessage(requestError)}</Alert>;
  }

  if (hasResolveParams && !resolvedData?.template && !overrideData) {
    return <Typography>Немає скрипта</Typography>;
  }

  if (!editorVersion) return <Typography>No draft data.</Typography>;

  const isActionPending =
    isSaving ||
    publishMutation.isPending ||
    saveOverrideMutation.isPending ||
    deleteOverrideMutation.isPending ||
    saveBaseMutation.isPending;

  const renderSectionGroup = ({
    title,
    sections,
    emptyText,
    defaultItemType,
    addLabel = "Додати блок",
  }) => {
    const groupIds = sections.map(({ section }) => section.localId);

    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddSection(defaultItemType)}
          >
            {addLabel}
          </Button>
        </Box>
        {sections.length ? (
          <Stack spacing={1}>
            {sections.map(({ section, items }, index) => (
              <Accordion
                key={section.localId}
                expanded={expandedSectionId === section.localId}
                onChange={handleAccordionChange(section.localId)}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={
                    <ArrowForwardIosSharpIcon sx={{ fontSize: "0.8rem" }} />
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                      {section.title || "Без назви"}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoveSectionInGroup(
                          section.localId,
                          "up",
                          groupIds
                        );
                      }}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMoveSectionInGroup(
                          section.localId,
                          "down",
                          groupIds
                        );
                      }}
                      disabled={index === sections.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteSection(section.localId);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <TextField
                      label="Назва секції"
                      defaultValue={section.title || ""}
                      onChange={(event) =>
                        updateSectionDraft(section.localId, {
                          title: event.target.value,
                        })
                      }
                      onBlur={(event) =>
                        handleUpdateSection(section.localId, {
                          title: event.target.value,
                        })
                      }
                      fullWidth
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(section.is_collapsed_by_default)}
                          onChange={(event) =>
                            handleUpdateSection(section.localId, {
                              is_collapsed_by_default: event.target.checked,
                            })
                          }
                        />
                      }
                      label="Згорнута за замовчуванням"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        handleAddItem(section.localId, defaultItemType)
                      }
                    >
                      Додати елемент
                    </Button>
                    {items.length ? (
                      <Stack spacing={2}>
                        {items.map((item, itemIndex) => (
                          <ItemEditor
                            key={item.localId}
                            item={item}
                            onDraftChange={(updates) =>
                              updateItemDraft(section.localId, item.localId, updates)
                            }
                            onChange={(updates) =>
                              handleUpdateItem(section.localId, item.localId, updates)
                            }
                            onDelete={() =>
                              handleDeleteItem(section.localId, item.localId)
                            }
                            onMoveUp={() =>
                              handleMoveItem(section.localId, item.localId, "up")
                            }
                            onMoveDown={() =>
                              handleMoveItem(
                                section.localId,
                                item.localId,
                                "down"
                              )
                            }
                            disableMoveUp={itemIndex === 0}
                            disableMoveDown={itemIndex === items.length - 1}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Немає елементів
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <Card sx={{ maxWidth: 1200, mx: "auto" }}>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Шаблон скрипта
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                <Chip
                  label={`Template #${resolvedTemplateId ?? editorVersion.template ?? "-"}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={isOverrideActive ? "Override" : `Version ${editorVersion.version}`}
                  size="small"
                  variant="outlined"
                />
                {!isOverrideActive && (
                  <Chip
                    label={editorVersion.status}
                    size="small"
                    color={
                      editorVersion.status === "PUBLISHED" ? "success" : "warning"
                    }
                    variant="outlined"
                  />
                )}
                {isDirty && <Chip label="Є незбережені зміни" size="small" />}
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={handleSaveAndPublish}
                disabled={isActionPending || isProductSaving}
              >
                Зберегти
              </Button>
              {previewUrl && (
                <Button
                  variant="outlined"
                  component="a"
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Перегляд як оператор
                </Button>
              )}
              {hasResolveParams && isOverrideActive && (
                <Button
                  variant="text"
                  color="warning"
                  onClick={handleResetOverride}
                  disabled={isActionPending}
                >
                  Скинути до базового
                </Button>
              )}
            </Stack>
          </Box>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {productError && (
            <Alert severity="warning">
              Не вдалося завантажити дані товару.
            </Alert>
          )}

          <Divider />

          <Grid
            container
            spacing={3}
            sx={{
              width: "100%",
              px: { xs: 1, sm: 2, md: 0 },
              pr: { xs: 1, sm: 2, md: 0 },
              py: { xs: 1, sm: 2 },
              mx: 0,
            }}
          >
            <Grid item xs={12} md={5}>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height="100%"
              >
                <Box
                  tabIndex={0}
                  onPaste={handleImagePaste}
                  onDrop={handleImageDrop}
                  onDragOver={handleImageDragOver}
                  onDragLeave={handleImageDragLeave}
                  onClick={handleOpenImageDialog}
                  sx={{
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: isImageDragOver ? "primary.main" : "divider",
                    p: 3,
                    textAlign: "center",
                    color: "text.secondary",
                    outline: "none",
                    cursor: "pointer",
                    backgroundColor: isImageDragOver
                      ? "rgba(99, 102, 241, 0.08)"
                      : "transparent",
                  }}
                >
                  {productLoading ? (
                    <Typography color="text.secondary">
                      Завантаження товару...
                    </Typography>
                  ) : productImageSrc ? (
                    <ProductImg
                      image={productImageSrc}
                      alt={productData?.title}
                    />
                  ) : (
                    <Box>
                      <Typography>Немає зображення</Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Вставте або перетягніть фото
                      </Typography>
                    </Box>
                  )}
                  {productImageSrc && isImageDragOver && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Відпустіть, щоб завантажити фото
                    </Typography>
                  )}
                </Box>

                {selectedColor && (
                  <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#6C63FF",
                        textTransform: "none",
                        borderRadius: "8px",
                        px: 3,
                      }}
                    >
                      Списати
                    </Button>
                    <Typography variant="body1">
                      Кількість: <strong>{selectedColor.quantity}</strong>
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenImageDialog}
                  >
                    Змінити фото
                  </Button>
                  {productImageFile && (
                    <Typography variant="caption" sx={{ ml: 1 }}>
                      {productImageFile.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={2} height="100%">
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Typography variant="h6">
                    {productData?.title || "Назва товару"}
                  </Typography>
                </Box>
                <TextField
                  label="Ціна"
                  size="small"
                  value={productPrice}
                  onChange={(event) => setProductPrice(event.target.value)}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ maxWidth: 180 }}
                />

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Атрибути
                    </Typography>
                    <IconButton size="small" onClick={handleOpenAttrMenu}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {productAttributes.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {productAttributes.map((attr, index) => {
                        const values = Array.isArray(attr.variations)
                          ? attr.variations
                          : [];
                        const label = values.length
                          ? `${attr.name}: ${values.join(", ")}`
                          : attr.name;
                        return (
                          <Chip
                            key={`${attr.name}-${index}`}
                            label={label}
                            size="small"
                            variant="outlined"
                          />
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Немає атрибутів
                    </Typography>
                  )}
                  <Menu
                    anchorEl={attrMenuAnchor}
                    open={Boolean(attrMenuAnchor)}
                    onClose={handleCloseAttrMenu}
                  >
                    <MenuItem
                      onClick={() => {
                        handleOpenProductModal("attribute_list");
                        handleCloseAttrMenu();
                      }}
                    >
                      Редагувати атрибути
                    </MenuItem>
                    <MenuItem onClick={handleOpenColorsDialog}>
                      Редагувати кольори
                    </MenuItem>
                  </Menu>
                </Box>

                {Array.isArray(productColors) && productColors.length ? (
                  <Stack spacing={1} flexWrap="wrap" alignItems="center">
                    <Color
                      selectedColor={selectedColor}
                      setSelectedColor={setSelectedColor}
                      colors={productColors}
                    />
                  </Stack>
                ) : null}
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={3}
              sx={{
                alignSelf: { md: "flex-start" },
                ml: { md: "auto" },
                pr: { md: 1.5 },
                maxWidth: { md: 360 },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 2,
                  p: 2,
                  width: "100%",
                }}
              >
                {renderSectionGroup({
                  title: "Питання по товару",
                  sections: faqSections,
                  emptyText: "Немає даних",
                  defaultItemType: "faq",
                  addLabel: "Додати блок",
                })}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Box sx={{ mt: 3, maxWidth: 1200, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Крос товари
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Autocomplete
              size="small"
              options={crossOptions}
              multiple
              disableCloseOnSelect
              value={selectedCrossOptions}
              inputValue={crossInput}
              onInputChange={(_, value) => setCrossInput(value)}
              onChange={(_, value) => {
                setSelectedCrossOptions(value || []);
              }}
              getOptionLabel={(option) => option.label || ""}
              getOptionDisabled={(option) =>
                crossAddedSet.has(String(option.product_id))
              }
              sx={{ minWidth: 260 }}
              renderTags={(value) =>
                value.length ? (
                  <Chip size="small" label={`Обрано: ${value.length}`} />
                ) : null
              }
              renderInput={(params) => (
                <TextField {...params} label="Пошук товару (CRM)" />
              )}
              renderOption={(props, option, { selected }) => {
                const added = crossAddedSet.has(String(option.product_id));
                return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    gap: 1,
                    opacity: added ? 0.8 : 1,
                    backgroundColor: selected
                      ? "rgba(34, 197, 94, 0.2)"
                      : added
                      ? "rgba(34, 197, 94, 0.12)"
                      : "transparent",
                    "&[aria-selected='true']": {
                      backgroundColor: "rgba(34, 197, 94, 0.2) !important",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(34, 197, 94, 0.16)",
                    },
                  }}
                >
                  <Avatar
                    src={option.image}
                    alt={option.title}
                    variant="rounded"
                    sx={{ width: 28, height: 28 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {option.title || "Без назви"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{option.product_id}
                    </Typography>
                  </Box>
                  {added && <Chip size="small" label="Додано" />}
                </Box>
                );
              }}
              freeSolo
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCrossProduct}
            >
              Додати
            </Button>
          </Box>
        </Box>
        {crossProductsForView.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              overflowX: "auto",
              pb: 1,
              scrollSnapType: "x mandatory",
              gridAutoFlow: "column",
              gridAutoColumns: "minmax(240px, 1fr)",
              gridTemplateRows: "repeat(2, minmax(0, 1fr))",
              gridAutoRows: "minmax(0, 1fr)",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 2,
              p: 2,
            }}
          >
            {crossProductsForView.map((product, index) => {
              const crmProductId = product.product_id;
              const crossCrmId = product.crm ?? effectiveCrmId;
              const metaKey =
                product?.product_id != null ? String(product.product_id) : null;
              const metaValue = metaKey
                ? crossMeta[metaKey] ?? product?.short_description ?? ""
                : "";
              const priceKey =
                product?.product_id ?? product?.script_product_id ?? product?.id;
              const priceValue =
                priceKey != null
                  ? crossPriceOverrides[String(priceKey)] ?? product?.price ?? ""
                  : product?.price ?? "";
              const routeId =
                product.script_product_id ?? product.id ?? crmProductId;
              const crossLink =
                routeId && crossCrmId && crmProductId
                  ? `/operator/scripts/${routeId}?crm=${crossCrmId}&product_id=${crmProductId}`
                  : null;
              return (
                <Card
                  key={product.cross_id || product.id || crmProductId || index}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    scrollSnapAlign: "start",
                    position: "relative",
                    animation: "crossCardIn 180ms ease",
                    "@keyframes crossCardIn": {
                      from: { opacity: 0, transform: "translateY(6px)" },
                      to: { opacity: 1, transform: "translateY(0)" },
                    },
                  }}
                >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    color: "text.primary",
                    ".dark-theme &": { color: "rgba(255,255,255,0.92)" },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      fontSize: "0.7rem",
                      backgroundColor: "rgba(0,0,0,0.04)",
                      color: "text.secondary",
                      ".dark-theme &": {
                        backgroundColor: "rgba(255,255,255,0.12)",
                        color: "rgba(255,255,255,0.75)",
                      },
                    }}
                  >
                    ID: {crmProductId ?? "—"}
                  </Box>
                  <Avatar
                    src={product.image}
                    alt={product.title}
                    variant="rounded"
                    sx={{ width: 64, height: 64 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>
                      {product.title || "Без назви"}
                    </Typography>
                    <CrossMetaInput
                        value={metaValue}
                        disabled={!metaKey}
                        onCommit={(nextValue) => {
                          if (!metaKey) return;
                          handleCrossMetaChange(metaKey, nextValue);
                        }}
                      />
                      <Box
                        sx={{
                          mt: 0.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {crossLink ? (
                          <Button
                            size="small"
                            component={Link}
                            to={crossLink}
                            sx={{ px: 0, minWidth: "auto" }}
                          >
                            Відкрити
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Немає посилання
                          </Typography>
                        )}
                        <Box sx={{ minWidth: 0, flexShrink: 0, ml: "auto" }}>
                          <CrossPriceInput
                            value={priceValue}
                            disabled={!priceKey}
                            onCommit={(nextValue) =>
                              handleCrossPriceChange(product, nextValue)
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveCrossProduct(product)}
                    sx={{ p: 0.5, mt: 1.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Немає крос товарів
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 3, maxWidth: 1200, mx: "auto" }}>
        <Tabs value={scriptTab} onChange={handleScriptTabChange}>
          <Tab label="Скрипт" />
          <Tab label="Характеристики" />
        </Tabs>
        <Box sx={{ mt: 2 }}>
          {scriptTab === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {renderSectionGroup({
                title: "Скрипт",
                sections: mainScriptSections,
                emptyText: "Немає скрипта",
                defaultItemType: "script",
                addLabel: "Додати секцію",
              })}
              {renderSectionGroup({
                title: "Заперечення",
                sections: objectionSections,
                emptyText: "Немає заперечень",
                defaultItemType: "objection",
                addLabel: "Додати секцію",
              })}
            </Box>
          ) : (
            <Accordion defaultExpanded sx={{ my: 1 }}>
              <AccordionSummary
                expandIcon={
                  <ArrowForwardIosSharpIcon sx={{ fontSize: "0.8rem" }} />
                }
              >
                <Typography fontWeight={600}>Опис</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label="Характеристики"
                  value={productCharacteristics}
                  onChange={(event) =>
                    handleCharacteristicsChange(event.target.value)
                  }
                  multiline
                  rows={6}
                  fullWidth
                />
                <Typography variant="caption" color="text.secondary">
                  Зміни товару зберігаються кнопкою “Зберегти”.
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={2200}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
      <ColorsDialog
        open={colorsDialogOpen}
        colors={productColors}
        onClose={handleCloseColorsDialog}
        onApply={setProductColors}
      />
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Фото товару</DialogTitle>
        <DialogContent dividers>
          <Box
            tabIndex={0}
            onPaste={handleImagePaste}
            onDrop={handleImageDrop}
            onDragOver={handleImageDragOver}
            onDragLeave={handleImageDragLeave}
            onClick={(event) => event.currentTarget.focus()}
            sx={{
              borderRadius: 2,
              border: "1px dashed",
              borderColor: isImageDragOver ? "primary.main" : "divider",
              p: 3,
              textAlign: "center",
              color: "text.secondary",
              outline: "none",
              backgroundColor: isImageDragOver
                ? "rgba(99, 102, 241, 0.08)"
                : "transparent",
            }}
          >
            {productImageSrc ? (
              <ProductImg image={productImageSrc} alt={productData?.title} />
            ) : (
              <Box>
                <Typography>Вставте або перетягніть фото</Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Підтримуються формати JPG/PNG/WebP
                </Typography>
              </Box>
            )}
            {isImageDragOver && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Відпустіть, щоб завантажити фото
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Button component="label" variant="outlined" size="small">
              Обрати файл
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleProductImageSelect}
              />
            </Button>
            {productImageSrc && (
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={handleRemoveProductImage}
              >
                Видалити фото
              </Button>
            )}
            {productImageFile && (
              <Typography variant="caption">{productImageFile.name}</Typography>
            )}
          </Box>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Після вибору фото натисніть “Зберегти”, щоб застосувати.
          </Typography>
          {imageDialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {imageDialogError}
            </Alert>
          )}
          {productError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {formatErrorMessage(productError)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>Закрити</Button>
        </DialogActions>
      </Dialog>
      <ProductInfoModal
        open={Boolean(productModalType)}
        onClose={handleCloseProductModal}
        modalType={productModalType}
        attributes={productAttributes}
        setAttributes={setProductAttributes}
        modalRef={productModalRef}
        setSelectedAttrIndex={setSelectedAttrIndex}
        selectedAttrIndex={selectedAttrIndex}
        setModalType={setProductModalType}
        setQuantity={setProductQuantity}
        quantity={productQuantity}
      />
    </Box>
  );
};

export default ScriptTemplateEditorPage;
